<template>
  <div class="rotation-opacity-sheet" @click.stop>
    <div class="sheet-header">
      <span class="sheet-title">{{ title }}</span>
      <button class="sheet-close" @click.stop="$emit('close')" @touchend.stop.prevent="$emit('close')">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </button>
    </div>
    
    <div class="sheet-content">
      <div class="slider-row">
        <input
          type="range"
          class="sheet-slider"
          :value="displayValue"
          @input="handleSliderInput"
          :min="min"
          :max="max"
          :step="step"
          :style="sliderStyle"
        />
      </div>
      
      <div class="input-row">
        <input
          type="number"
          class="sheet-input"
          :value="displayValue"
          @input="handleNumberInput"
          @blur="handleNumberBlur"
          :min="min"
          :max="max"
          :step="step"
        />
        <span class="input-suffix">{{ suffix }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RotationOpacitySheet',
  props: {
    title: { type: String, required: true },
    value: { type: Number, required: true },
    min: { type: Number, default: 0 },
    max: { type: Number, default: 100 },
    step: { type: Number, default: 1 },
    suffix: { type: String, default: '' },
    isPercentage: { type: Boolean, default: false }
  },
  emits: ['update', 'close', 'start'],
  computed: {
    displayValue() {
      if (this.isPercentage) {
        return Math.round(this.value * 100);
      }
      return Math.round(this.value);
    },
    sliderStyle() {
      const percentage = ((this.displayValue - this.min) / (this.max - this.min)) * 100;
      return {
        background: `linear-gradient(to right, #4A90D9 ${percentage}%, #e5e7eb ${percentage}%)`
      };
    }
  },
  methods: {
    handleSliderInput(e) {
      this.$emit('start');
      const rawValue = parseFloat(e.target.value);
      const outputValue = this.isPercentage ? rawValue / 100 : rawValue;
      this.$emit('update', outputValue);
    },
    handleNumberInput(e) {
      let rawValue = parseFloat(e.target.value);
      if (isNaN(rawValue)) return;
      rawValue = Math.max(this.min, Math.min(this.max, rawValue));
      const outputValue = this.isPercentage ? rawValue / 100 : rawValue;
      this.$emit('start');
      this.$emit('update', outputValue);
    },
    handleNumberBlur(e) {
      let rawValue = parseFloat(e.target.value);
      if (isNaN(rawValue)) rawValue = this.min;
      rawValue = Math.max(this.min, Math.min(this.max, rawValue));
      const outputValue = this.isPercentage ? rawValue / 100 : rawValue;
      this.$emit('update', outputValue);
    }
  }
}
</script>

<style scoped lang="scss">
.rotation-opacity-sheet {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  border-radius: 16px 16px 0 0;
  padding: 16px 20px 24px;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  z-index: 10001;
  
  .sheet-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 12px;
    border-bottom: 1px solid #eee;
    
    .sheet-title {
      font-size: 16px;
      font-weight: 600;
      color: #333;
    }
    
    .sheet-close {
      background: none;
      border: none;
      padding: 4px;
      cursor: pointer;
      color: #666;
      display: flex;
      align-items: center;
      justify-content: center;
      
      &:active {
        color: #333;
      }
    }
  }
  
  .sheet-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
    
    .slider-row {
      width: 100%;
      
      .sheet-slider {
        width: 100%;
        height: 8px;
        -webkit-appearance: none;
        appearance: none;
        border-radius: 4px;
        outline: none;
        
        &::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #4A90D9;
          cursor: pointer;
          border: 3px solid #fff;
          box-shadow: 0 2px 6px rgba(74, 144, 217, 0.4);
        }
        
        &::-moz-range-thumb {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #4A90D9;
          cursor: pointer;
          border: 3px solid #fff;
          box-shadow: 0 2px 6px rgba(74, 144, 217, 0.4);
        }
      }
    }
    
    .input-row {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      
      .sheet-input {
        width: 80px;
        padding: 10px 12px;
        font-size: 18px;
        font-weight: 500;
        text-align: center;
        border: 2px solid #e0e0e0;
        border-radius: 8px;
        outline: none;
        -webkit-appearance: none;
        -moz-appearance: textfield;
        
        &:focus {
          border-color: #007AFF;
        }
        
        &::-webkit-inner-spin-button,
        &::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
      }
      
      .input-suffix {
        font-size: 18px;
        font-weight: 500;
        color: #666;
      }
    }
  }
}
</style>
